import Globe from 'globe.gl';
import type { GlobeInstance, Arc, Ember } from '@/types';
import { CONFIG, DEFAULT_POV } from '@/config';
import { store } from '@/state';
import { createEmberObject } from './embers';

/**
 * Initialize the globe visualization
 * @param container - Container element for the globe
 */
export function initGlobe(container: HTMLElement): void {
  const globe = Globe() as unknown as GlobeInstance;

  // Configure globe appearance
  globe
    .globeImageUrl(CONFIG.globeImageUrl)
    .backgroundColor('#030308')
    .atmosphereColor('#01a982')
    .atmosphereAltitude(0.15)
    .showAtmosphere(true)(container);

  // Set initial camera position
  globe.pointOfView({ lat: DEFAULT_POV.lat, lng: DEFAULT_POV.lng, altitude: DEFAULT_POV.altitude });

  // Configure controls
  globe.controls().autoRotate = true;
  globe.controls().autoRotateSpeed = 0.3;
  globe.controls().enableZoom = true;

  // Configure arcs layer
  globe
    .arcsData([])
    .arcColor((d: Arc) => d.color)
    .arcStroke(0.4)
    .arcDashLength((d: Arc) => d.dashLength)
    .arcDashGap((d: Arc) => d.dashGap)
    .arcDashAnimateTime(500)
    .arcAltitude((d: Arc) => d.altitude);

  // Configure embers (objects) layer
  globe
    .objectsData([])
    .objectLat((d: Ember) => d.lat)
    .objectLng((d: Ember) => d.lng)
    .objectAltitude(0.01)
    .objectThreeObject((d: Ember) => createEmberObject(d));

  // Configure labels (Las Vegas marker)
  globe
    .labelsData([
      {
        lat: 36.1699,
        lng: -115.1398,
        text: '',
        altitude: 0.01,
      },
    ])
    .labelLat((d) => d.lat)
    .labelLng((d) => d.lng)
    .labelText((d) => d.text)
    .labelAltitude((d) => d.altitude)
    .labelSize(0)
    .labelDotRadius(0.5)
    .labelColor(() => 'rgba(1, 169, 130, 1)')
    .labelResolution(2);

  // Store globe instance
  store.set('globe', globe);

  // Setup resize observer
  setupResizeObserver(container);

  console.log('FlowAtlas Mission Control initialized');
}

/**
 * Setup resize observer for the globe container
 * @param container - Container element
 */
function setupResizeObserver(container: HTMLElement): void {
  const globeContainer = container.closest('.globe-container') as HTMLElement;

  if (!globeContainer) {
    console.warn('Globe container not found for resize observer');
    return;
  }

  const resizeObserver = new ResizeObserver(() => {
    const globe = store.get('globe');
    if (globe) {
      globe.width(globeContainer.clientWidth);
      globe.height(globeContainer.clientHeight);
    }
  });

  resizeObserver.observe(globeContainer);
}

/**
 * Reset globe to default view
 * @param transitionMs - Transition duration in milliseconds
 */
export function resetGlobeView(transitionMs: number = 1000): void {
  const globe = store.get('globe');
  if (globe) {
    globe.pointOfView(
      { lat: DEFAULT_POV.lat, lng: DEFAULT_POV.lng, altitude: DEFAULT_POV.altitude },
      transitionMs
    );
  }
}

/**
 * Toggle globe auto-rotation
 * @returns New rotation state
 */
export function toggleRotation(): boolean {
  const globe = store.get('globe');
  if (!globe) return false;

  const newState = !globe.controls().autoRotate;
  globe.controls().autoRotate = newState;
  store.set('savedAutoRotate', newState);
  return newState;
}

/**
 * Set globe rotation speed
 * @param speed - Rotation speed multiplier
 */
export function setRotationSpeed(speed: number): void {
  const globe = store.get('globe');
  if (globe) {
    globe.controls().autoRotateSpeed = Math.max(0.1, Math.min(2.0, speed));
  }
}

/**
 * Get current rotation speed
 * @returns Current rotation speed
 */
export function getRotationSpeed(): number {
  const globe = store.get('globe');
  return globe?.controls().autoRotateSpeed ?? 0.3;
}

/**
 * Zoom the globe
 * @param delta - Zoom delta (positive = zoom in, negative = zoom out)
 * @param transitionMs - Transition duration
 */
export function zoomGlobe(delta: number, transitionMs: number = 300): void {
  const globe = store.get('globe');
  if (!globe) return;

  const pov = globe.pointOfView();
  const newAltitude = Math.max(0.5, Math.min(5, pov.altitude - delta));
  globe.pointOfView({ ...pov, altitude: newAltitude }, transitionMs);
}

/**
 * Toggle pause state
 * @returns New pause state
 */
export function togglePause(): boolean {
  const state = store.getState();
  const globe = state.globe;
  const newPaused = !state.isPaused;

  if (globe) {
    if (newPaused) {
      // Save current auto-rotate state and pause
      store.set('savedAutoRotate', globe.controls().autoRotate);
      globe.controls().autoRotate = false;
    } else {
      // Restore auto-rotate state
      globe.controls().autoRotate = state.savedAutoRotate;
    }
  }

  store.set('isPaused', newPaused);
  return newPaused;
}

/**
 * Take a screenshot of the globe
 */
export function takeScreenshot(): void {
  const canvas = document.querySelector('#globe-container canvas') as HTMLCanvasElement;
  if (!canvas) return;

  canvas.toBlob((blob) => {
    if (!blob) return;

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `flowatlas-${Date.now()}.png`;
    link.click();
    URL.revokeObjectURL(url);
  });
}
